import { Button, Div, Label, useWindowSize } from "@grassrootly/rn-core";
import { GameStatues } from "@grassrootly/api";
import { useConfirmDialog } from "../../../layout/components/confirm-dialog";
import { NextGameProps } from "./NextGame.types";
import { useNavigation } from "@react-navigation/native";
import { useGameVotes } from "../hooks/useGameVotes";
import { useAuth } from "../../guard/useAuth";

export default function NextGameActions({ game, setGameAttendancy, updateAttendancyInProgress }: NextGameProps) {
  const { prompt } = useConfirmDialog();
  const { isMobile } = useWindowSize();
  const navigation = useNavigation();
  const { playerVoted } = useGameVotes({ gameId: game.game_id });
  const { user } = useAuth();

  const votedAlready = playerVoted(user?.id!);

  const goToVote = () => navigation?.navigate("vote", { id: game.game_id });
  const declineGame = () => setGameAttendancy({ gameId: game.game_id, attending: false });
  const acceptGame = () => setGameAttendancy({ gameId: game.game_id, attending: true });

  const reDecline = () =>
    prompt({
      title: "Change attendence",
      message: "You have accepted the attendece to this game. Do you want to change it and decline?",
      onYes: declineGame,
    });

  const reAccept = () =>
    prompt({
      title: "Change attendence",
      message: "You have declined the attendece to this game. Do you want to change it and accept?",
      onYes: acceptGame,
    });

  return (
    <Div classNames={["flexRow", "alignContentCenter", "justifyContentCenter", "p8", "textCenter", "w-{100%}"]}>
      <Div classNames={["w-{100%}"]}>
        {/* SCHEDULED GAME */}
        {game.game_status === GameStatues.SCHEDULED &&
          (game.player_attended ? (
            <Div
              classNames={[
                "dFlex",
                "alignItemsCenter",
                "justifyContentCenter",
                `${!isMobile ? "flexRow gap16" : "flexCol gap8"}`,
                "textCenter",
              ]}
            >
              <Label type={!isMobile ? "l1Bold" : "l3Regular"} classNames={["colorPrimaryMain"]}>
                You joined! Have a great game!
              </Label>
              <Div classNames={["mt-{8}"]}>
                <Button
                  classNames={["btn", "btnOutlinePrimary", "bgNeutralDarkerDark"]}
                  isLoading={updateAttendancyInProgress}
                  disabled={updateAttendancyInProgress}
                  onPress={reDecline}
                  right={{ name: "change_circle", size: 24, classNames: ["colorPrimaryMain"] }}
                >
                  <Label type={!isMobile ? "l1Regular" : "l3Regular"} classNames={["colorPrimaryMain"]}>
                    Change RSVP
                  </Label>
                </Button>
              </Div>
            </Div>
          ) : game.player_attended !== null ? (
            <Div
              classNames={[
                "dFlex",
                "alignItemsCenter",
                "justifyContentCenter",
                `${!isMobile ? "flexRow gap16" : "flexCol gap8"}`,
                "textCenter",
              ]}
            >
              <Label type={!isMobile ? "l1Regular" : "l3Regular"} classNames={["colorDangerMain"]}>
                Too bad! Hope you play next one!
              </Label>
              <Div classNames={["mt-{8}"]}>
                <Button
                  classNames={["btn", "btnOutlineDanger", "bgNeutralDarkerDark"]}
                  onPress={reAccept}
                  isLoading={updateAttendancyInProgress}
                  disabled={updateAttendancyInProgress}
                  right={{ name: "change_circle", size: 24, classNames: ["colorDangerMain"] }}
                >
                  <Label type={!isMobile ? "l1Regular" : "l3Regular"} classNames={["colorDangerMain"]}>
                    Change RSVP
                  </Label>
                </Button>
              </Div>
            </Div>
          ) : (
            <Div
              classNames={[
                "dFlex",
                "alignItemsCenter",
                "justifyContentCenter",
                "flexRow",
                `${!isMobile ? "gap16" : "gap8"}`,
                "textCenter",
                "w-{100%}",
              ]}
            >
              <Button
                title="Skip Match"
                left={{ name: "close" }}
                onPress={declineGame}
                isLoading={updateAttendancyInProgress}
                disabled={updateAttendancyInProgress}
                classNames={["btn", "btnDanger", "flex1"]}
              />
              <Button
                title="Join Match"
                left={{ name: "check" }}
                classNames={["btn", "btnPrimary", "flex1"]}
                onPress={acceptGame}
                isLoading={updateAttendancyInProgress}
                disabled={updateAttendancyInProgress}
              />
            </Div>
          ))}

        {/* CONFIRMED GAME (centered block; no vote count) */}
        {game.game_status === GameStatues.CONFIRMED && (
          <Div classNames={["mt-{-17}", "btn", "br-{8}", "pv-{8}", "ph-{12}", "borderPrimary", "bw-{1}", "bgNeutralDarkerDark", "alignItemsCenter", "justifyContentCenter", "textCenter", "alignSelfCenter"]}>
            <Label classNames={["textCenter"]}>KICK-OFF {game.game_status}</Label>
          </Div>
        )}

        {/* FINISHED GAME (centered block; count below regardless of attendance) */}
        {game.game_status === GameStatues.FINISHED && (
          <Div classNames={["flexColumn", "alignItemsCenter", "justifyContentCenter"]}>
            {game.player_attended ? (
              <Button
                classNames={[
                  "btn",
                  "mt-{-20}",
                  "alignSelfCenter",
                  ...(votedAlready ? ["btnOutlinePrimary", "bgNeutralDarkerDark"] : ["btnPrimary"]),
                ]}
                title={votedAlready ? "Voted" : "Vote"}
                disabled={votedAlready}
                onPress={goToVote}
                left={{ name: "emoji_events" }}
              />
            ) : (
              <Div
                classNames={[
                  "mt-{-17}",
                  "btn",
                  "br-{8}",
                  "pv-{8}",
                  "ph-{12}",
                  "borderPrimary",
                  "bw-{1}",
                  "bgNeutralDarkerDark",
                  "alignItemsCenter",
                  "justifyContentCenter",
                  "textCenter",
                  "alignSelfCenter",
                ]}
              >
                <Label classNames={["textCenter"]}>{GameStatues.FINISHED}</Label>
              </Div>
            )}
          </Div>
        )}

        {/* FINALIZED GAME (centered block; count below) */}
        {game.game_status === GameStatues.FINALIZED && (
          <Div classNames={["flexColumn", "alignItemsCenter", "justifyContentCenter", "mt-{-17}"]}>
            <Div classNames={["btn", "br-{8}", "pv-{8}", "ph-{12}", "borderPrimary", "bw-{1}", "bgNeutralDarkerDark", "alignItemsCenter", "justifyContentCenter", "textCenter", "alignSelfCenter"]}>
              <Label classNames={["textCenter"]}>{GameStatues.FINISHED}</Label>
            </Div>
          </Div>
        )}

        {/* CANCELED GAME */}
        {game.game_status === GameStatues.CANCELED && (
          <Div
            classNames={["mt-{-20}", "btn", "btnOutlineDanger", "br-{8}", "pv-{8}", "ph-{12}", "bgNeutralDarkerDark"]}
          >
            <Label classNames={["colorDangerMain"]}>{game.game_status}</Label>
          </Div>
        )}
      </Div>
    </Div>
  );
}
